import { englishOnlyMarketingRoutes } from "./localized-routes";
import { careerRoles } from "@/content/careers";

export type Locale = "en" | "ms" | "zh";

export interface LocaleConfig {
  label: string;
  nativeLabel: string;
  hreflang: string;
  openGraphLocale: string;
  prefix: string;
}

export const defaultLocale: Locale = "en";

export const localeConfigs: Record<Locale, LocaleConfig> = {
  en: {
    label: "English",
    nativeLabel: "English",
    hreflang: "en-MY",
    openGraphLocale: "en_MY",
    prefix: "",
  },
  ms: {
    label: "Malay",
    nativeLabel: "Bahasa Melayu",
    hreflang: "ms-MY",
    openGraphLocale: "ms_MY",
    prefix: "/ms",
  },
  zh: {
    label: "Chinese",
    nativeLabel: "\u4e2d\u6587",
    hreflang: "zh-MY",
    openGraphLocale: "zh_MY",
    prefix: "/zh",
  },
};

export const locales = Object.keys(localeConfigs) as Locale[];
export type TranslatedLocale = Exclude<Locale, typeof defaultLocale>;
export const translatedLocales: TranslatedLocale[] = ["ms", "zh"];

const localizedPathnamePattern = /^\/(en|ms|zh)(?=\/|$)/;

function normalizePathname(pathname: string): string {
  if (!pathname || pathname === "/") {
    return "/";
  }

  const cleanPathname = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return cleanPathname.replace(/\/+$/, "") || "/";
}

function ensureTrailingSlash(pathname: string): string {
  return pathname === "/" ? "/" : `${normalizePathname(pathname)}/`;
}

export function isLocale(locale: string): locale is Locale {
  return locales.includes(locale as Locale);
}

export function isTranslatedLocale(locale: string): locale is TranslatedLocale {
  return translatedLocales.includes(locale as TranslatedLocale);
}

export function stripLocalePrefix(pathname: string): string {
  const normalizedPathname = normalizePathname(pathname);
  const strippedPathname = normalizedPathname.replace(
    localizedPathnamePattern,
    "",
  );

  return strippedPathname || "/";
}

export function isBlogRoute(pathname: string): boolean {
  const unlocalizedPathname = stripLocalePrefix(pathname);

  return (
    unlocalizedPathname === "/blog" ||
    unlocalizedPathname.startsWith("/blog/")
  );
}

/**
 * True when a route is built in English only, so the language switcher must not
 * offer /ms/ and /zh/ variants that were never generated.
 *
 * Derived from englishOnlyMarketingRoutes rather than hand-listed. The previous
 * hardcoded list had drifted: "invest" was added to the registry but never here,
 * so /invest/ showed BM and 中文 buttons that 404'd. Anything added to the
 * registry from now on is covered automatically.
 *
 * The two blog routes below are not in the registry (blog posts are registered
 * separately) so they stay explicit.
 */
const EXTRA_ENGLISH_ONLY = [
  "/blog/ai-seo-agent-malaysia",
  "/blog/seo-price-malaysia-2026",
];

export function isEnglishOnlyRoute(pathname: string): boolean {
  // Trailing slashes vary by caller — normalise before comparing.
  const path = stripLocalePrefix(pathname).replace(/\/+$/, "") || "/";

  if (EXTRA_ENGLISH_ONLY.includes(path)) return true;
  // Individual role pages (/careers/<slug>/) are English-only; the /careers/
  // overview itself is localized.
  if (careerRoles.some((role) => path === `/careers/${role.slug}`)) return true;

  return englishOnlyMarketingRoutes.some(({ route }) => {
    const routePath = `/${route}`;
    return path === routePath || path.startsWith(`${routePath}/`);
  });
}

export function getLocaleFromPathname(pathname: string): Locale {
  const normalizedPathname = normalizePathname(pathname);
  const localeMatch = normalizedPathname.match(localizedPathnamePattern);
  const locale = localeMatch?.[1];

  return locale && isLocale(locale) ? locale : defaultLocale;
}

export function getLocalizedPathname(pathname: string, locale: Locale): string {
  // Split off any query string or hash BEFORE localising: ensureTrailingSlash
  // would otherwise append the slash to the end of the whole string, turning
  // "/enterprise/enquiry/?brand=lenovo-malaysia" into "…?brand=lenovo-malaysia/"
  // and silently corrupting the parameter value.
  const suffixIndex = pathname.search(/[?#]/);
  const suffix = suffixIndex === -1 ? "" : pathname.slice(suffixIndex);
  const path = suffixIndex === -1 ? pathname : pathname.slice(0, suffixIndex);
  const unlocalizedPathname = stripLocalePrefix(path);

  // A route built in English only has no /ms/ or /zh/ variant, so its localised
  // form IS the English one. Without this, any component linking to such a route
  // from a translated page produced a URL that was never generated — the footer's
  // "AI SEO Agent" link 404'd on every /ms/ and /zh/ page, as did the Singapore
  // partner link. Handling it here fixes every caller at once rather than asking
  // each one to remember.
  if (locale === defaultLocale || isEnglishOnlyRoute(unlocalizedPathname)) {
    return ensureTrailingSlash(unlocalizedPathname) + suffix;
  }

  return (
    unlocalizedPathname === "/"
      ? `${localeConfigs[locale].prefix}/`
      : ensureTrailingSlash(
          `${localeConfigs[locale].prefix}${unlocalizedPathname}`,
        )
  ) + suffix;
}

export function getCanonicalPathname(
  pathname: string,
  locale: Locale = defaultLocale,
): string {
  return getLocalizedPathname(pathname, locale);
}

export function getLanguageAlternates(
  pathname: string,
): Record<string, string> | undefined {
  if (isBlogRoute(pathname) || isEnglishOnlyRoute(pathname)) {
    return undefined;
  }

  const alternates = locales.reduce<Record<string, string>>(
    (accumulator, locale) => {
      accumulator[localeConfigs[locale].hreflang] = getLocalizedPathname(
        pathname,
        locale,
      );

      return accumulator;
    },
    {},
  );

  alternates["x-default"] = getLocalizedPathname(pathname, defaultLocale);

  return alternates;
}
